import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart } from 'lucide-react';

import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useAuthUI } from '../../context/AuthUIContext';
import CartLineItem from './CartLineItem';
import Button from '../ui/Button';
import { formatPrice } from '../../utils/money';

// Slide-over cart. Mounted once in Layout; visibility from CartContext.isOpen.
export default function CartDrawer() {
  const { items, subtotal, count, balanceDue, amountDueNow, source, isOpen, closeDrawer } = useCart();
  const { user } = useAuth();
  const { openLogin } = useAuthUI();
  const navigate = useNavigate();

  const goCheckout = () => {
    closeDrawer();
    if (user) navigate('/checkout');
    else openLogin(); // guests must log in before checkout
  };

  // Close on Escape while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeDrawer]);

  const go = (path) => {
    closeDrawer();
    navigate(path);
  };

  return (
    <>
      <div
        className={`ss-drawer-overlay ${isOpen ? 'is-open' : ''}`}
        onClick={closeDrawer}
        aria-hidden={!isOpen}
      />
      <aside
        className={`ss-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        aria-hidden={!isOpen}
      >
        <div className="ss-drawer__head">
          <h2>Your cart{count > 0 ? ` (${count})` : ''}</h2>
          <button type="button" className="ss-drawer__close" aria-label="Close cart" onClick={closeDrawer}>
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="ss-drawer__body">
          {items.length === 0 ? (
            <div className="ss-cart-empty">
              <ShoppingCart size={40} aria-hidden="true" />
              <p style={{ marginTop: 'var(--ss-space-3)' }}>Your cart is empty.</p>
            </div>
          ) : (
            items.map((i) => <CartLineItem key={i.productId} item={i} />)
          )}
        </div>

        {items.length > 0 && (
          <div className="ss-drawer__foot">
            {balanceDue > 0 ? (
              <>
                <div className="ss-summary-line ss-summary-line--muted">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="ss-cart-subtotal">
                  <span>Due now</span>
                  <span>{formatPrice(amountDueNow)}</span>
                </div>
                <div className="ss-summary-line ss-summary-line--muted">
                  <span>Balance due later</span>
                  <span>{formatPrice(balanceDue)}</span>
                </div>
              </>
            ) : (
              <div className="ss-cart-subtotal">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
            )}
            <Button onClick={goCheckout}>Checkout</Button>
            <Button variant="ghost" onClick={() => go('/cart')}>View Cart</Button>
            {source === 'guest' && (
              <p className="ss-cart-note">Log in to save your cart across devices.</p>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
